// Кошелёк игрока: его счёт, его ставка и выигрыш последнего раунда.
//
// Денег кошелёк не считает. Счёт ведёт бэкенд, см. ADR-09: он списывает ставку, зачисляет
// выплату и присылает остаток на итоге раунда. Кошелёк только держит присланное и показывает
// его нижней полосе.
//
// Ставку он держит сам: её выбирает игрок, а бэкенд узнаёт о ней из запроса спина. Лестницу
// ставок задаёт описание игры, см. ADR-33.
//
// Ставка уходит со счёта в начале раунда, а не в его конце: игрок нажал на спин и должен
// увидеть это тем же движением. Итог раунда ставит счёт по присланному остатку, и на этом
// показ и правда сходятся.
//
// Выигрыш кошелёк принимает по спинам, а не одной суммой за раунд: цепочка респинов идёт
// секунды, и полоса, стоящая всё это время неподвижно, называет досмотренный спин нулём.
// Складывается это ровно: выплата раунда и есть выплаты его спинов вместе с бонусом.

import type { Cents, GameConfig } from '../api/gameConfig';
import type { RoundResult } from '../api/spin';

/** Кошелёк игрока. */
export class Wallet {
  /** Ставки, из которых игрок выбирает, по возрастанию. Пустой список — ставка одна. */
  readonly stakes: readonly Cents[];

  #balance: Cents;
  #stake: Cents;
  #lastWin: Cents = 0;

  /** Кого разбудить, когда что-то из этого поменялось. */
  readonly #listeners = new Set<() => void>();

  /**
   * @param config Описание игры. Из него берётся лестница ставок.
   * @param balance Счёт игрока на этот миг.
   * @param stake Ставка, которой игрок сыграл прошлый раунд.
   */
  constructor(config: GameConfig, balance: Cents, stake: Cents) {
    this.stakes = config.stakes ?? [stake];
    this.#balance = balance;
    this.#stake = stake;
  }

  /** Счёт игрока. */
  get balance(): Cents {
    return this.#balance;
  }

  /** Ставка за спин. */
  get stake(): Cents {
    return this.#stake;
  }

  /** Выигрыш последнего раунда. На ходу раунда он растёт со спинами. Ноль — платить пока не за что. */
  get lastWin(): Cents {
    return this.#lastWin;
  }

  /** Хватает ли счёта на ставку. Ложь — крутить не на что, и кнопка спина гаснет. */
  get affords(): boolean {
    return this.#balance >= this.#stake;
  }

  /** Есть ли ставка дороже нынешней. */
  get canRaise(): boolean {
    return this.#step(1) !== null;
  }

  /** Есть ли ставка дешевле нынешней. */
  get canLower(): boolean {
    return this.#step(-1) !== null;
  }

  /** Подписывает слушателя на изменения: на кошелёк смотрят и полоса, и кнопка спина. */
  listen(onChange: () => void): void {
    this.#listeners.add(onChange);
  }

  /** Берёт следующую ставку из лестницы. Дороже некуда — ставка остаётся прежней. */
  raise(): void {
    this.#moveTo(this.#step(1));
  }

  /** Берёт предыдущую ставку из лестницы. Дешевле некуда — ставка остаётся прежней. */
  lower(): void {
    this.#moveTo(this.#step(-1));
  }

  /**
   * Списывает ставку в начале раунда и снимает выигрыш прошлого.
   *
   * Настоящее списание уже сделал бэкенд, тем же запросом. Здесь оно только показывается:
   * иначе счёт стоял бы неподвижно, пока крутятся барабаны.
   */
  bet(): void {
    this.#balance -= this.#stake;
    this.#lastWin = 0;
    this.#notify();
  }

  /**
   * Ставит выигрыш одного спина: он идёт и на счёт, и в поле последнего выигрыша.
   *
   * Зачислил его бэкенд одной суммой за весь раунд. Здесь он только показывается, и по спинам,
   * а не по раунду: игрок досмотрел сумму на поле и ждёт её же в полосе, а не через три респина.
   *
   * За раунд выигрыши складываются: поле называется LAST WIN, и последний тут раунд, а не спин.
   * Итог раунда доводит счёт до присланного остатка, см. settle.
   */
  win(payout: Cents): void {
    if (payout === 0)
      return;

    this.#balance += payout;
    this.#lastWin += payout;
    this.#notify();
  }

  /**
   * Ставит счёт по итогу раунда.
   *
   * Остаток берётся у бэкенда как есть, а не досчитывается: он и есть настоящий. Сложенные
   * по спинам выигрыши с ним сходятся, а выплату бонуса он приносит сам: её на спины
   * не разложить, бонус платит раз за раунд.
   *
   * Раунд без сессии остатка не несёт, и тогда счёт остаётся с тем, что сложили спины.
   */
  settle(round: RoundResult): void {
    if (round.after !== undefined)
      this.#balance = round.after.balance;

    this.#lastWin = round.payout;
    this.#notify();
  }

  /** Будит всех, кто смотрит в кошелёк. */
  #notify(): void {
    for (const listener of this.#listeners)
      listener();
  }

  /** Ставка на шаг в заданную сторону. Null — лестница там кончилась. */
  #step(direction: number): Cents | null {
    const next: number = this.stakes.indexOf(this.#stake) + direction;

    return next >= 0 && next < this.stakes.length ? this.stakes[next] : null;
  }

  #moveTo(stake: Cents | null): void {
    if (stake === null)
      return;

    this.#stake = stake;
    this.#notify();
  }
}
